import type { Router, RouteRecordRaw } from 'vue-router';
import { buildModuleRoutes } from '@/app-router/build-module-routes';
import { mergeAppRoutes } from '@/app-router/merge-app-routes';
import type { GetModulePageComponent, MergeAppRoutesContext, ModuleRegistryEntry, ModuleRoutingContext } from '@/app-router/types';

export interface CreateAppRoutesOptions {
  registry: readonly ModuleRegistryEntry[];
  getComponent: GetModulePageComponent;
  mergeContext: MergeAppRoutesContext;
  routingContext?: ModuleRoutingContext;
}

export interface SyncModuleRoutesOptions extends CreateAppRoutesOptions {
  fixedRoutes: RouteRecordRaw[];
}

export function createAppRoutes(
  fixedRoutes: RouteRecordRaw[],
  settings: Record<string, unknown>,
  options: CreateAppRoutesOptions,
): RouteRecordRaw[] {
  const moduleRoutes = buildModuleRoutes(settings, options.registry, options.getComponent, options.routingContext);
  return mergeAppRoutes(moduleRoutes, fixedRoutes, options.mergeContext);
}

export async function reconcileRouterWithBrowserUrl(router: Router) {
  if (typeof window === 'undefined') {
    return;
  }

  const base = router.options?.history?.base ?? '/';
  let locationPath = window.location.pathname;

  if (base !== '/' && locationPath.startsWith(base)) {
    locationPath = locationPath.slice(base.length);
    if (!locationPath.startsWith('/')) {
      locationPath = `/${locationPath}`;
    }
  }

  const target = `${locationPath}${window.location.search}${window.location.hash}`;

  if (target && router.currentRoute.value.fullPath !== target) {
    await router.replace(target);
  }
}

async function ensureActiveRouteResolved(router: Router) {
  const matched = router.currentRoute.value.matched ?? [];
  const onlyCatchAll = matched.length > 0 && matched.every((record) => record.name === 'error404');

  // A sole error404/catch-all match is not a real resolution — rematch after module sync.
  if (matched.length && !onlyCatchAll) {
    return;
  }

  await reconcileRouterWithBrowserUrl(router);

  const rematched = router.currentRoute.value.matched ?? [];
  const stillOnlyCatchAll = rematched.length > 0 && rematched.every((record) => record.name === 'error404');

  if (rematched.length && !stillOnlyCatchAll) {
    return;
  }

  const fallbackPath = router.currentRoute.value.fullPath;
  if (fallbackPath) {
    await router.replace(fallbackPath);
  }
}

function routeMetaChanged(left: RouteRecordRaw | undefined, right: RouteRecordRaw | undefined) {
  return JSON.stringify(left?.meta ?? {}) !== JSON.stringify(right?.meta ?? {});
}

function shouldRematchAfterModuleSync(activeRouteName: unknown, moduleRouteNames: ReadonlySet<string>): boolean {
  if (activeRouteName === 'error404') {
    return true;
  }

  return Boolean(activeRouteName && moduleRouteNames.has(activeRouteName as string));
}

export async function syncModuleRoutes(
  router: Router,
  settings: Record<string, unknown>,
  options: SyncModuleRoutesOptions,
): Promise<number> {
  const moduleRoutes = buildModuleRoutes(settings, options.registry, options.getComponent, options.routingContext);
  const existingRoutes = router.getRoutes();
  const { moduleRouteNames } = options.mergeContext;

  const moduleRoutesChanged = [...moduleRouteNames].some((routeName) => {
    const nextRoute = moduleRoutes.find((route) => route.name === routeName);
    const currentRoute = existingRoutes.find((route) => route.name === routeName);

    if (!nextRoute && currentRoute) {
      return true;
    }

    if (nextRoute && !currentRoute) {
      return true;
    }

    if (nextRoute && currentRoute && (nextRoute.path !== currentRoute.path || routeMetaChanged(nextRoute, currentRoute))) {
      return true;
    }

    return false;
  });

  if (moduleRoutesChanged) {
    const activeRouteName = router.currentRoute.value.name;
    const shouldReNavigate = shouldRematchAfterModuleSync(activeRouteName, moduleRouteNames);
    const currentFullPath = router.currentRoute.value.fullPath;

    for (const routeName of moduleRouteNames) {
      if (router.hasRoute(routeName)) {
        router.removeRoute(routeName);
      }
    }

    for (const route of moduleRoutes) {
      router.addRoute(route);
    }

    if (shouldReNavigate && currentFullPath) {
      await router.replace(currentFullPath);
    }
  }

  await ensureActiveRouteResolved(router);

  return moduleRoutes.length;
}

export function getFixedRoutesExcludingModules(fixedRoutes: RouteRecordRaw[], mergeContext: MergeAppRoutesContext): RouteRecordRaw[] {
  return mergeAppRoutes([], fixedRoutes, mergeContext);
}
